/**
 * state.js — Single source of truth for the viewer application.
 *
 * Observers (UI, overlay) subscribe to state changes via on().
 * No direct mutation outside of the update() function.
 */

/** @typedef {{ x: number, y: number, w: number, h: number }} Rect */
/** @typedef {{ id: string, rect: Rect, label: string, note: string }} Annotation */
/** @typedef {{ text: Array, tables: Array, layout: Array } | null} Artifacts */

/**
 * Per-page state bucket.
 * @returns {{ artifacts: Artifacts, annotations: Annotation[] }}
 */
function makePageState() {
  return { artifacts: null, annotations: [] }
}

const _listeners = new Map()
let _state = {
  /** @type {string|null} */
  docId: null,
  pageCount: 0,
  currentPage: 0,      // 0-indexed
  scale: 1.0,          // current zoom scale (1.0 = fit-to-width)
  fitScale: 1.0,       // scale that fits page in viewport
  /** @type {Map<number, ReturnType<makePageState>>} */
  pages: new Map(),
  /** @type {{id:string,status:string,progress:{done:number,total:number}}|null} */
  job: null,
}

/**
 * Get a read-only snapshot of current state.
 */
export function getState() {
  return _state
}

/**
 * Get (or create) per-page state.
 * @param {number} pageIdx 0-indexed
 */
export function getPageState(pageIdx) {
  if (!_state.pages.has(pageIdx)) {
    _state.pages.set(pageIdx, makePageState())
  }
  return _state.pages.get(pageIdx)
}

/**
 * Merge partial state updates and notify listeners.
 * @param {Partial<typeof _state>} partial
 */
export function update(partial) {
  const prev = _state
  _state = { ..._state, ...partial }
  for (const [event, cbs] of _listeners) {
    // Notify on specific key changes
    if (event in partial || event === '*') {
      for (const cb of cbs) cb(_state, prev)
    }
  }
}

/**
 * Subscribe to state changes.
 * @param {string} event  Key name to watch, or '*' for any change.
 * @param {(state: typeof _state, prev: typeof _state) => void} cb
 * @returns {() => void}  Unsubscribe function.
 */
export function on(event, cb) {
  if (!_listeners.has(event)) _listeners.set(event, new Set())
  _listeners.get(event).add(cb)
  return () => _listeners.get(event).delete(cb)
}

/**
 * Add an artifact result for a page.
 * @param {number} pageIdx
 * @param {Artifacts} artifacts
 */
export function setPageArtifacts(pageIdx, artifacts) {
  const ps = getPageState(pageIdx)
  ps.artifacts = artifacts
  // Notify overlay listeners that page content changed
  _dispatch('pageArtifacts', { pageIdx, artifacts })
}

/**
 * Add a user annotation to a page.
 * @param {number} pageIdx
 * @param {Annotation} ann
 */
export function addAnnotation(pageIdx, ann) {
  const ps = getPageState(pageIdx)
  ps.annotations.push(ann)
  _dispatch('annotations', { pageIdx })
}

/**
 * Remove an annotation by id from a page.
 */
export function removeAnnotation(pageIdx, annId) {
  const ps = getPageState(pageIdx)
  ps.annotations = ps.annotations.filter(a => a.id !== annId)
  _dispatch('annotations', { pageIdx })
}

/**
 * Reset all document state (on new document load).
 */
export function resetDocument() {
  _state = {
    ..._state,
    docId: null,
    pageCount: 0,
    currentPage: 0,
    scale: 1.0,
    fitScale: 1.0,
    pages: new Map(),
    job: null,
  }
  _dispatch('*', {})
}

// ── Internal ──────────────────────────────────────────────────────────────

function _dispatch(event, payload) {
  const cbs = _listeners.get(event) || []
  for (const cb of cbs) cb(payload, _state)
  const star = _listeners.get('*') || []
  for (const cb of star) cb(_state, _state)
}
